import React, { useId, useState } from 'react';
import { InputSwitch, InputSwitchChangeEvent } from 'primereact/inputswitch';
import './SwitchAtom.scss';

export interface SwitchAtomProps {
  /** Visible label, bound to the switch's input. */
  label?: string;
  /** Controlled checked state. When provided, parent owns the state. */
  checked?: boolean;
  /** Initial checked value when used uncontrolled. Ignored if `checked` is provided. */
  initialChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** `compact` is the 32×18 switch for dense cards (spec rollup-combine D107). */
  size?: 'default' | 'compact';
  className?: string;
  /** Id of the switch's input. Minted per instance when omitted, so `label` always binds. */
  inputId?: string;
  /** Accessible name when there is no visible `label` (e.g. a switch beside a field label). */
  ariaLabel?: string;
  /** Same as `ariaLabel`, in attribute form. */
  'aria-label'?: string;
  /** Id of an element that names the switch. */
  'aria-labelledby'?: string;
}

/**
 * PrimeReact's InputSwitch (10.9) gives its ROOT `<div>` `role="checkbox"` + `aria-checked`
 * AND its real `<input>` `role="switch"` + `aria-checked` — two toggles, one nested in the
 * other (axe `nested-interactive`), the outer one unnamed (`aria-toggle-field-name`). The
 * input is the control: it takes focus and receives the name. The root is styling only, so
 * its role, state and the name PrimeReact also copies onto it are removed.
 */
const ROOT_IS_PRESENTATION = {
  role: undefined,
  'aria-checked': undefined,
  'aria-label': undefined,
  'aria-labelledby': undefined,
};

/** A visible label binds by `htmlFor`; only a label-less switch needs an ARIA name. */
function switchNameAttrs(label?: string, labelledBy?: string, name?: string): { 'aria-labelledby'?: string; 'aria-label'?: string } {
  if (label) return {};
  if (labelledBy) return { 'aria-labelledby': labelledBy };
  if (name) return { 'aria-label': name };
  return {};
}

const SwitchAtom: React.FC<SwitchAtomProps> = ({
  label,
  checked,
  initialChecked = false,
  onChange,
  disabled = false,
  size = 'default',
  className = '',
  inputId,
  ariaLabel,
  'aria-label': ariaLabelAttr,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const isControlled = checked !== undefined;
  const [internalChecked, setInternalChecked] = useState<boolean>(initialChecked);
  const value = isControlled ? checked : internalChecked;
  const mintedId = useId();
  const id = inputId ?? mintedId;
  const nameAttrs = switchNameAttrs(label, ariaLabelledBy, ariaLabel ?? ariaLabelAttr);

  const handleChange = (e: InputSwitchChangeEvent) => {
    const newValue = e.value ?? false;
    if (!isControlled) {
      setInternalChecked(newValue);
    }
    onChange?.(newValue);
  };

  return (
    <div className={`switch-atom ${size === 'compact' ? 'switch-atom--compact' : ''} ${className}`.trim().replace(/\s+/g, ' ')}>
      <InputSwitch
        inputId={id}
        checked={value}
        onChange={handleChange}
        disabled={disabled}
        {...nameAttrs}
        pt={{ root: ROOT_IS_PRESENTATION as never }}
      />
      {label && (
        <label htmlFor={id} className="switch-atom__label">
          {label}
        </label>
      )}
    </div>
  );
};

export default SwitchAtom;
